import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Mail, Menu, MessageCircle, Phone, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { COMPANY, NAV_LINKS, PHONE_LINK, WHATSAPP_LINK } from "../../lib/constants";
import { trackEvent } from "../../lib/api";

const slug = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-");
const isActive = (item, pathname) => pathname === item.href || (item.children || []).some((c) => c.href === pathname);
const linkCls = (on) => `inline-flex items-center gap-0.5 whitespace-nowrap rounded-lg px-2 py-2 text-[14.5px] font-medium transition-colors 2xl:px-2.5 ${on ? "text-maas" : "text-ink hover:text-maas"}`;

const Dropdown = ({ item, id }) => (
  <div id={id} className="absolute left-0 top-full z-50 pt-2 reveal">
    <ul className={`card grid gap-0.5 p-2 ${item.children.length > 5 ? "w-[560px] grid-cols-2" : "w-[320px]"}`} style={{ boxShadow: "var(--shadow-2)" }}>
      {item.children.map((c) => (
        <li key={c.href}>
          <Link to={c.href} data-testid={`mega-item-${c.href.replace(/[^a-z0-9]+/g, "-")}`} className="block rounded-xl px-3 py-2.5 hover:bg-slate-50 focus-visible:bg-slate-50">
            <span className="block text-[14.5px] font-semibold text-ink">{c.label}</span>
            <span className="block text-[13px] text-slate-500">{c.desc}</span>
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// Desktop: openen bij hover of klik/Enter, sluiten bij verlaten (kleine vertraging), Escape of focus buiten het menu.
const MegaItem = ({ item, open, setOpen, pathname }) => {
  const id = `mega-${slug(item.label)}`;
  const timer = useRef(null);
  if (!item.children) return <NavLink to={item.href} data-testid={`nav-link-${id}`} className={({ isActive: a }) => linkCls(a)}>{item.label}</NavLink>;
  const expanded = open === item.label;
  const enter = () => { clearTimeout(timer.current); setOpen(item.label); };
  const leave = () => { timer.current = setTimeout(() => setOpen((o) => (o === item.label ? null : o)), 140); };
  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setOpen((o) => (o === item.label ? null : o))}>
      <button type="button" aria-expanded={expanded} aria-controls={id} aria-haspopup="true" data-testid={`nav-link-${id}`}
        onClick={() => setOpen(expanded ? null : item.label)} onKeyDown={(e) => e.key === "ArrowDown" && (e.preventDefault(), setOpen(item.label))}
        className={linkCls(isActive(item, pathname))}>
        {item.label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {expanded && <Dropdown item={item} id={id} />}
    </div>
  );
};

const MobileGroup = ({ item, expanded, setExpanded, close }) => {
  const on = expanded === item.label;
  const id = `mobile-group-${slug(item.label)}`;
  return (
    <div className="border-b border-slate-100">
      <button type="button" aria-expanded={on} aria-controls={id} onClick={() => setExpanded(on ? null : item.label)} data-testid={`mobile-nav-group-${slug(item.label)}`}
        className="flex min-h-[52px] w-full items-center justify-between px-2 text-[16px] font-semibold text-ink">
        {item.label}
        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${on ? "rotate-180 text-maas" : ""}`} aria-hidden="true" />
      </button>
      {on && (
        <ul id={id} className="mb-2 rounded-xl bg-slate-50 py-1.5">
          {item.children.map((c) => (
            <li key={c.href}><Link to={c.href} onClick={close} className="flex min-h-[44px] items-center px-4 text-[15px] text-slate-700 hover:text-maas" data-testid={`mobile-nav-item-${c.href.replace(/[^a-z0-9]+/g, "-")}`}>{c.label}</Link></li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Mobiel: smalle lade (de pagina blijft zichtbaar), uitklapbare groepen, één primaire knop.
const MobileNav = ({ open, setOpen }) => {
  const [expanded, setExpanded] = useState(null);
  const close = () => setOpen(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-[88vw] max-w-[380px] flex-col gap-0 p-0 [&>button]:hidden" data-testid="mobile-nav">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="flex h-[60px] shrink-0 items-center justify-between border-b border-slate-200 px-4">
          <img src="/logo-maas.svg" alt="Maas Kozijn" className="h-7 w-auto" width="96" height="33" />
          <button type="button" onClick={close} aria-label="Menu sluiten" data-testid="mobile-nav-close" className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <nav aria-label="Mobiel menu" className="flex-1 overflow-y-auto px-3 py-2">
          {NAV_LINKS.map((item) => item.children
            ? <MobileGroup key={item.label} item={item} expanded={expanded} setExpanded={setExpanded} close={close} />
            : <Link key={item.href} to={item.href} onClick={close} className="flex min-h-[52px] items-center border-b border-slate-100 px-2 text-[16px] font-semibold text-ink" data-testid={`mobile-nav-link-${slug(item.label)}`}>{item.label}</Link>)}
        </nav>
        <div className="shrink-0 border-t border-slate-200 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
          <Link to="/offerte-aanvragen" onClick={() => { trackEvent("cta_quote_click", { location: "mobile_nav" }); close(); }} className="btn-primary w-full" data-testid="mobile-nav-cta">Ontvang vrijblijvend een prijsindicatie</Link>
          <div className="mt-3 grid grid-cols-3 gap-2 text-[13px] font-semibold text-ink">
            <a href={PHONE_LINK} onClick={() => trackEvent("click_phone", { page_type: "mobile_nav" })} className="flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-slate-200" data-testid="mobile-nav-phone"><Phone className="h-4 w-4 text-maas" aria-hidden="true" /> Bellen</a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_whatsapp", { page_type: "mobile_nav" })} className="flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-slate-200" data-testid="mobile-nav-whatsapp"><MessageCircle className="h-4 w-4 text-whatsapp" aria-hidden="true" /> WhatsApp</a>
            <a href={`mailto:${COMPANY.email}`} onClick={() => trackEvent("click_email", { page_type: "mobile_nav" })} className="flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-slate-200" data-testid="mobile-nav-email"><Mail className="h-4 w-4 text-maas" aria-hidden="true" /> E-mail</a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Vaste header: 84 px bovenaan, 64 px tijdens scrollen (desktop); 60 px op mobiel. De spacer houdt de inhoud op zijn plek.
export const SiteHeader = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setOpen(null); setMobileOpen(false); }, [pathname]);
  useEffect(() => { document.documentElement.toggleAttribute("data-nav-open", mobileOpen); }, [mobileOpen]);
  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 24);
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(null);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, []);

  return (
    <>
      <header ref={ref} data-testid="site-header" data-compact={compact}
        className={`fixed inset-x-0 top-0 z-50 border-b bg-white/95 backdrop-blur-md transition-[box-shadow,border-color] duration-300 ${compact ? "border-slate-200 shadow-[0_6px_20px_-12px_rgba(15,37,55,0.25)]" : "border-slate-200/70"}`}>
        <div className={`container-tight flex h-[60px] items-center justify-between gap-4 transition-[height] duration-300 ease-out ${compact ? "lg:h-16" : "lg:h-[84px]"}`}>
          <Link to="/" aria-label="Maas Kozijn — naar de homepage" data-testid="header-logo" className="shrink-0">
            <img src="/logo-maas.svg" alt="Maas Kozijn" width="120" height="41" className={`h-8 w-auto transition-[height] duration-300 ${compact ? "lg:h-8" : "lg:h-10"}`} />
          </Link>
          <nav aria-label="Hoofdmenu" className="hidden items-center xl:flex">
            {NAV_LINKS.map((item) => <MegaItem key={item.label} item={item} open={open} setOpen={setOpen} pathname={pathname} />)}
          </nav>
          <div className="flex items-center gap-2 xl:gap-3">
            <a href={PHONE_LINK} onClick={() => trackEvent("click_phone", { page_type: "header" })} data-testid="header-phone" aria-label={`Bel ${COMPANY.phoneDisplay}`}
              className="hidden items-center gap-2 whitespace-nowrap rounded-lg px-2 py-2 text-[14px] font-semibold leading-none text-ink hover:text-maas md:inline-flex xl:h-10 xl:w-10 xl:justify-center xl:border xl:border-slate-200 xl:px-0 2xl:h-auto 2xl:w-auto 2xl:border-0 2xl:px-2">
              <Phone className="h-4 w-4 shrink-0 text-maas" aria-hidden="true" /> <span className="xl:hidden 2xl:inline" data-testid="header-phone-number">{COMPANY.phoneDisplay}</span>
            </a>
            <Link to="/offerte-aanvragen" onClick={() => trackEvent("cta_quote_click", { location: "header" })} data-testid="header-cta"
              className={`btn-primary hidden whitespace-nowrap !px-4 transition-[min-height,padding] duration-300 md:inline-flex ${compact ? "!min-h-[40px] !py-2" : "!min-h-[44px] !py-2.5"}`}>Prijsindicatie</Link>
            <button type="button" onClick={() => setMobileOpen(true)} aria-label="Menu openen" aria-expanded={mobileOpen} data-testid="mobile-nav-open"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 text-[14px] font-semibold text-ink hover:bg-slate-50 xl:hidden">
              <Menu className="h-5 w-5" aria-hidden="true" /> <span className="hidden sm:inline">Menu</span>
            </button>
          </div>
        </div>
        <MobileNav open={mobileOpen} setOpen={setMobileOpen} />
      </header>
      <div aria-hidden="true" className="h-[61px] lg:h-[85px]" data-testid="site-header-spacer" />
    </>
  );
};
